import { Check } from 'lucide-react'
import { motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { SectionHeading } from './SectionHeading'

interface Feature {
  title: string
  description: string
}

interface FeatureListProps {
  title?: string
  subtitle?: string
  items: Feature[]
  centered?: boolean
  light?: boolean
  className?: string
}

export function FeatureList({
  title,
  subtitle,
  items,
  centered = true,
  light = false,
  className,
}: FeatureListProps) {
  return (
    <div className={className}>
      {(title || subtitle) && (
        <SectionHeading
          title={title || ''}
          subtitle={subtitle}
          centered={centered}
          light={light}
        />
      )}

      <div
        className={cn(
          'flex flex-wrap justify-center gap-6 md:gap-8',
          (title || subtitle) && 'mt-10 md:mt-12'
        )}
      >
        {items.map((item, index) => (
          <motion.div
            key={item.title}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.4, delay: index * 0.08 }}
            className="flex w-full gap-4 md:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.334rem)]"
          >
            <span
              className={cn(
                'mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                light ? 'bg-primary/20 text-primary' : 'bg-primary/15 text-primary'
              )}
            >
              <Check className="h-3.5 w-3.5" strokeWidth={3} />
            </span>
            <div>
              <h3
                className={cn(
                  'font-medium text-base',
                  light ? 'text-white' : 'text-ink'
                )}
              >
                {item.title}
              </h3>
              <p
                className={cn(
                  'mt-1 text-sm leading-relaxed',
                  light ? 'text-white/70' : 'text-muted'
                )}
              >
                {item.description}
              </p>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  )
}
